import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useMsal } from "@azure/msal-react";
import { createPortal } from 'react-dom';
import { useUser } from '../contexts/UserContext';
import { 
  DocumentProfile, 
  getAllProfiles, 
  createProfile,
  deleteProfile,
  CreateProfileDto,
  createRule,
  deleteRule,
  CreateRuleDto,
  addConditionToRule,
  deleteCondition,
  CreateConditionDto
} from '../services/profileApiService';
import { 
    Search, 
    Plus, 
    Trash2, 
    Download, 
    Save, 
    Route, 
    Bell,
    ChevronDown,
    X,
    ExternalLink
} from 'lucide-react';
import { ACUMATICA_FIELDS, DOC_INTELLIGENCE_FIELDS, RULE_OPERATORS } from '../constants/mappingOptions';

const CustomSelect = ({ 
    value, 
    options, 
    onChange,
    className = ""
  }: { 
    value: string, 
    options: { label: string, value: any }[], 
    onChange: (val: any) => void,
    className?: string
  }) => {
    const [isOpen, setIsOpen] = useState(false);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const [coords, setCoords] = useState({ top: 0, left: 0, width: 0 });
    const selectedOption = options.find(o => o.value === value);
  
    const toggleOpen = () => {
      if (!isOpen && buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        setCoords({ top: rect.bottom + 4, left: rect.left, width: rect.width });
      }
      setIsOpen(!isOpen);
    };
  
    return (
      <div className={`relative shrink-0 ${className}`}>
        <button
          ref={buttonRef}
          type="button"
          onClick={toggleOpen}
          className="w-full flex items-center justify-between px-3 h-10 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg text-sm font-medium text-slate-900 dark:text-white focus:ring-2 focus:ring-primary/50 transition-all"
        >
          <span className="truncate">{selectedOption?.label || value}</span>
          <ChevronDown size={16} className="text-gray-500" />
        </button>
  
        {isOpen && (
          createPortal(
            <>
              <div className="fixed inset-0 z-[9998]" onClick={() => setIsOpen(false)} />
              <div 
                className="fixed z-[9999] bg-white dark:bg-slate-800 rounded-xl border border-slate-100 dark:border-slate-700 shadow-xl overflow-y-auto max-h-[50vh] custom-scrollbar animate-in zoom-in-95 duration-200"
                style={{ top: coords.top, left: coords.left, width: coords.width }}
              >
              {options.map(op => (
                <button
                  key={op.value}
                  type="button"
                  onClick={() => {
                    onChange(op.value);
                    setIsOpen(false);
                  }}
                  className={`w-full text-left px-3 py-2.5 text-sm font-medium transition-colors ${
                    op.value === value 
                      ? 'bg-primary/10 text-primary font-bold' 
                      : 'text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700'
                  }`}
                >
                  {op.label}
                </button>
              ))}
            </div>
            </>,
            document.body
          )
        )}
      </div>
    );
  };

export function MappingsAdminPage() {
    const { isAdmin } = useUser();
    const { instance, accounts } = useMsal();

    const [profiles, setProfiles] = useState<DocumentProfile[]>([]);
    const [selectedProfileId, setSelectedProfileId] = useState<number | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [newProfileName, setNewProfileName] = useState('');
    const [newModelId, setNewModelId] = useState('');
    
    // New Rule State
    const [outputField, setOutputField] = useState(ACUMATICA_FIELDS[0]?.value || '');
    const [outputValue, setOutputValue] = useState('');
    const [sourceField, setSourceField] = useState(DOC_INTELLIGENCE_FIELDS[0]?.value || '');
    const [operator, setOperator] = useState(RULE_OPERATORS[0]?.value || '');
    const [inputValue, setInputValue] = useState('');

    // New Condition State
    const [addingConditionToRuleId, setAddingConditionToRuleId] = useState<number | null>(null);
    const [newConditionSourceField, setNewConditionSourceField] = useState(DOC_INTELLIGENCE_FIELDS[0]?.value || '');
    const [newConditionOperator, setNewConditionOperator] = useState(RULE_OPERATORS[0]?.value || '');
    const [newConditionInputValue, setNewConditionInputValue] = useState('');

    // Load Profiles
    const fetchProfiles = async () => {
        if (!accounts[0]) return;
        setIsLoading(true);
        try {
            const data = await getAllProfiles(instance, accounts[0]);
            setProfiles(data);
            if (data.length > 0 && !selectedProfileId) {
                setSelectedProfileId(data[0].id);
            }
        } catch (err) {
            console.error(err);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (isAdmin && accounts[0]) {
            fetchProfiles();
        }
    }, [isAdmin, instance, accounts]);

    const selectedProfile = useMemo(() => 
        profiles.find(p => p.id === selectedProfileId), 
    [profiles, selectedProfileId]);

    const filteredProfiles = useMemo(() => 
        profiles.filter(p => p.profileName.toLowerCase().includes(searchQuery.toLowerCase())),
    [profiles, searchQuery]);

    const handleSaveProfile = async () => {
        if (!newProfileName || !newModelId) return;

        try {
            const newProfile: CreateProfileDto = {
                profileName: newProfileName,
                documentIntelligenceModelId: newModelId
            };
            const created = await createProfile(instance, accounts[0], newProfile);
            if (created) {
                const newProfileFull: DocumentProfile = {
                    id: created.id,
                    profileName: newProfile.profileName,
                    documentIntelligenceModelId: newProfile.documentIntelligenceModelId,
                    rules: []
                };
                setProfiles(prev => [...prev, newProfileFull]);
                setSelectedProfileId(created.id);
                setIsModalOpen(false);
                setNewProfileName('');
                setNewModelId('prebuilt-invoice');
            } else {
                fetchProfiles();
                setIsModalOpen(false);
            }
        } catch (error) {
            alert("Failed to create profile.");
        }
    };

    const handleDeleteProfile = async (e: React.MouseEvent, id: number) => {
        e.stopPropagation();
        if (!window.confirm("Are you sure you want to delete this profile?")) return;
        try {
            await deleteProfile(instance, accounts[0], id);
            setProfiles(prev => prev.filter(p => p.id !== id));
            if (selectedProfileId === id) {
                setSelectedProfileId(null);
            }
        } catch (error) {
            alert("Failed to delete profile.");
        }
    };

    const handleAddRule = async () => {
        if (!selectedProfile) return;
        
        const newRuleDto: CreateRuleDto = {
            profileId: selectedProfile.id,
            ruleName: `Set ${outputField} to ${outputValue}`,
            outputField,
            outputValue,
            priority: selectedProfile.rules.length + 1,
            logicalOperator: 'AND',
            conditions: []
        };

        if (sourceField && operator && inputValue) {
            newRuleDto.conditions.push({
                sourceField,
                operator,
                inputValue
            });
        }

        try {
            const createdRule = await createRule(instance, accounts[0], newRuleDto);
            
            if (createdRule) {
                // Refresh profiles to get updated rules structure
                fetchProfiles();
                
                // Reset form
                setOutputValue('');
                setInputValue('');
            }
        } catch (error) {
            console.error(error);
            alert("Failed to create rule.");
        }
    };

    const handleDeleteRule = async (ruleId: number) => {
        if (!window.confirm("Delete this rule?")) return;
        try {
            await deleteRule(instance, accounts[0], ruleId);
            // Update local state
            setProfiles(prev => prev.map(p => {
                if (p.id === selectedProfileId) {
                    return { ...p, rules: p.rules.filter(r => r.id !== ruleId) };
                }
                return p;
            }));
        } catch (error) {
            alert("Failed to delete rule.");
        }
    };

    const handleAddCondition = async (ruleId: number) => {
        if (!newConditionSourceField || !newConditionOperator || !newConditionInputValue) {
            alert("Please fill out all fields for the new condition.");
            return;
        }

        const newConditionDto: CreateConditionDto = {
            ruleId: ruleId,
            sourceField: newConditionSourceField,
            operator: newConditionOperator,
            inputValue: newConditionInputValue
        };

        try {
            await addConditionToRule(instance, accounts[0], newConditionDto);
            
            // Refresh data and close form
            fetchProfiles();
            setAddingConditionToRuleId(null);

            // Reset form
            setNewConditionSourceField(DOC_INTELLIGENCE_FIELDS[0]?.value || '');
            setNewConditionOperator(RULE_OPERATORS[0]?.value || '');
            setNewConditionInputValue('');

        } catch (error) {
            console.error("Failed to add condition:", error);
            alert("Failed to add condition.");
        }
    };

    const handleDeleteCondition = async (conditionId: number, ruleId: number) => {
        if (!window.confirm("Delete this condition?")) return;
        try {
            await deleteCondition(instance, accounts[0], conditionId);
            // Update local state to reflect deletion
            setProfiles(prev => prev.map(p => {
                if (p.id === selectedProfileId) {
                    const updatedRules = p.rules.map(r => {
                        if (r.id === ruleId) {
                            return { ...r, conditions: r.conditions.filter(c => c.id !== conditionId) };
                        }
                        return r;
                    });
                    return { ...p, rules: updatedRules };
                }
                return p;
            }));
        } catch (error) {
            alert("Failed to delete condition.");
        }
    };

    if (!isAdmin) return <div className="p-8">Access Denied</div>;

    return (
        <div className="flex h-full flex-col bg-background-light dark:bg-background-dark font-display text-slate-900 dark:text-slate-100 antialiased overflow-hidden">
            {/* Top Navigation Bar */}
            <header className="flex h-16 items-center justify-between border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-6 shrink-0 z-20">
                <div className="flex items-center gap-3">
                    <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary text-white">
                        <Route size={20} />
                    </div>
                    <h2 className="text-lg font-bold tracking-tight">Data Mappings</h2>
                </div>
                {/* <div className="flex items-center gap-3">
                    <button className="flex items-center justify-center rounded-lg h-10 w-10 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors">
                        <Bell size={20} />
                    </button>
                    <div className="h-8 w-[1px] bg-slate-200 dark:bg-slate-800 mx-1"></div>
                    <div className="flex items-center gap-3 pl-2">
                        <div className="text-right hidden sm:block">
                            <p className="text-xs font-semibold leading-none">Admin User</p>
                            <p className="text-[10px] text-slate-500 uppercase mt-1">System Architect</p>
                        </div>
                        <div className="h-9 w-9 rounded-full bg-slate-200 dark:bg-slate-800 flex items-center justify-center text-xs font-bold">
                            AD
                        </div>
                    </div>
                </div> */}
            </header>

            <div className="flex flex-1 overflow-hidden">
                {/* Sidebar */}
                <aside className="w-[30%] min-w-[320px] max-w-[400px] flex flex-col border-r border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/50">
                    <div className="p-4 flex gap-2 items-center">
                        <div className="relative group flex-1">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-primary transition-colors" size={18} />
                            <input 
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full rounded-lg border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 py-2 pl-10 pr-4 text-sm focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-all" 
                                placeholder="Search profiles..." 
                                type="text"
                            />
                        </div>
                        <button 
                            onClick={() => setIsModalOpen(true)}
                            className="flex items-center justify-center h-9 w-9 rounded-lg bg-primary text-white shadow-sm hover:bg-primary/90 transition-colors shrink-0"
                        >
                            <Plus size={20} />
                        </button>
                    </div>

                    <div className="flex-1 overflow-y-auto custom-scrollbar px-4 pb-4">
                        <div className="space-y-2">
                            {isLoading ? (
                                <div className="text-center py-4 text-slate-400 text-sm">Loading...</div>
                            ) : filteredProfiles.map(profile => (
                                <div 
                                    key={profile.id}
                                    onClick={() => setSelectedProfileId(profile.id)}
                                    className={`group flex items-center justify-between rounded-lg border-l-4 p-4 transition-all cursor-pointer ${
                                        selectedProfileId === profile.id 
                                        ? 'border-primary bg-white dark:bg-slate-800 shadow-sm' 
                                        : 'border-transparent bg-transparent hover:bg-white/60 dark:hover:bg-slate-800/40'
                                    }`}
                                >
                                    <div className="flex flex-col gap-1 overflow-hidden">
                                        <span className={`truncate font-semibold ${selectedProfileId === profile.id ? 'text-slate-900 dark:text-slate-100' : 'text-slate-600 dark:text-slate-400'}`}>
                                            {profile.profileName}
                                        </span>
                                        <span className="text-[11px] text-slate-500 flex items-center gap-1">
                                            {profile.documentIntelligenceModelId}
                                        </span>
                                    </div>
                                    <button 
                                        onClick={(e) => handleDeleteProfile(e, profile.id)}
                                        className="text-slate-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                                    >
                                        <Trash2 size={18} />
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>

                    
                </aside>

                {/* Main Content */}
                <main className="flex-1 flex flex-col bg-white dark:bg-slate-950 overflow-hidden">
                    {selectedProfile ? (
                        <>
                            {/* Main Header */}
                            <div className="px-8 pt-8 pb-4">
                                <div className="flex items-end justify-between">
                                    <div>
                                        <h1 className="text-2xl font-bold text-slate-900 dark:text-white">{selectedProfile.profileName}</h1>
                                        <p className="text-sm text-slate-500 mt-1">
                                            Model ID: {selectedProfile.documentIntelligenceModelId}
                                            <a
                                                href={`https://documentintelligence.ai.azure.com/studio/custommodel/projects/${selectedProfile.documentIntelligenceModelId}/label`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="inline-flex items-center gap-1 ml-2 text-primary hover:text-primary/80 transition-colors"
                                                title="Open in Document Intelligence Studio"
                                            >
                                                <ExternalLink size={14} />
                                                <span className="text-xs font-semibold">Open in Studio</span>
                                            </a>
                                        </p>
                                    </div>                                    
                                </div>
                            </div>

                            {/* Quick Create Row */}
                            <div className="px-8 py-6 border-b border-slate-100 dark:border-slate-900 bg-slate-50/50 dark:bg-slate-900/30">
                                <div className="flex items-center gap-3 bg-white dark:bg-slate-800 p-4 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-x-auto no-scrollbar">
                                    <span className="text-xs font-black text-slate-400 uppercase tracking-widest shrink-0">Set</span>
                                    <CustomSelect 
                                        value={outputField}
                                        onChange={setOutputField}
                                        options={ACUMATICA_FIELDS}
                                        className="min-w-[140px]"
                                    />
                                    
                                    <span className="text-xs font-black text-slate-400 uppercase tracking-widest shrink-0">To</span>
                                    <input 
                                        value={outputValue}
                                        onChange={(e) => setOutputValue(e.target.value)}
                                        className="w-32 rounded-lg border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 text-sm focus:ring-primary focus:border-primary py-2 px-3" 
                                        placeholder="Value..." 
                                        type="text"
                                    />
                                    
                                    <span className="text-xs font-black text-slate-400 uppercase tracking-widest shrink-0">If</span>
                                    <CustomSelect 
                                        value={sourceField}
                                        onChange={setSourceField}
                                        options={DOC_INTELLIGENCE_FIELDS}
                                        className="min-w-[140px]"
                                    />
                                    
                                    <CustomSelect 
                                        value={operator}
                                        onChange={setOperator}
                                        options={RULE_OPERATORS}
                                        className="w-32"
                                    />
                                    
                                    <input 
                                        value={inputValue}
                                        onChange={(e) => setInputValue(e.target.value)}
                                        className="w-40 rounded-lg border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 text-sm focus:ring-primary focus:border-primary py-2 px-3" 
                                        placeholder="Compare value..." 
                                        type="text"
                                    />
                                    
                                    <button 
                                        onClick={handleAddRule}
                                        disabled={!outputValue}
                                        className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary text-white hover:bg-primary/90 shrink-0 shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        <Plus size={20} />
                                    </button>
                                </div>
                            </div>

                            {/* Rules List */}
                            <div className="flex-1 overflow-y-auto custom-scrollbar px-8 py-6">
                                <div className="flex items-center justify-between mb-4">
                                    <h3 className="text-sm font-bold text-slate-400 uppercase tracking-widest">Active Mapping Rules ({selectedProfile.rules.length})</h3>
                                    <div className="h-px flex-1 bg-slate-100 dark:bg-slate-900 ml-4"></div>
                                </div>
                                
                                <div className="space-y-3">
                                    {selectedProfile.rules.length === 0 && (
                                        <div className="text-center py-10 text-slate-400 italic">No rules defined yet. Use the bar above to add one.</div>
                                    )}
                                    {selectedProfile.rules.map((rule, idx) => (
                                        <div key={rule.id} className="group relative p-4 rounded-xl border border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900/40 hover:border-primary/30 hover:bg-slate-50 dark:hover:bg-slate-900 transition-all">
                                            <div className="flex items-start justify-between">
                                                <div className="flex items-start gap-4 text-sm font-medium">
                                                    <div className="flex h-6 w-6 items-center justify-center rounded bg-slate-100 dark:bg-slate-800 text-[10px] font-bold text-slate-500 shrink-0">{idx + 1}</div>
                                                    <div className='flex flex-col gap-3'>
                                                        {/* Output part of the rule */}
                                                        <div className="flex items-center gap-3 flex-wrap">
                                                            <span className="text-slate-400 font-bold uppercase text-[10px]">Set</span>
                                                            <span className="px-2 py-1 rounded bg-blue-50 dark:bg-blue-900/30 text-primary border border-blue-100 dark:border-blue-900/50 font-mono text-xs">{rule.outputField}</span>
                                                            <span className="text-slate-400 font-bold uppercase text-[10px]">To</span>
                                                            <span className="px-2 py-1 rounded bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 font-mono text-xs">"{rule.outputValue}"</span>
                                                        </div>

                                                        {/* Conditions part of the rule */}
                                                        <div className="flex flex-col items-start gap-3">
                                                            {rule.conditions.length > 0 && <span className="text-slate-400 font-bold uppercase text-[10px] mt-1">If</span>}
                                                            {rule.conditions.map((cond, cIdx) => (
                                                                <div key={cond.id} className="flex items-center gap-3 flex-wrap pl-4 border-l-2 border-slate-100 dark:border-slate-800">
                                                                    {cIdx > 0 && <span className="text-slate-400 font-bold uppercase text-[10px]">{rule.logicalOperator}</span>}
                                                                    <span className="px-2 py-1 rounded bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-500 border border-amber-100 dark:border-amber-900/30 font-mono text-xs">{cond.sourceField}</span>
                                                                    <span className="text-slate-400 font-medium italic text-xs">{cond.operator}</span>
                                                                    <span className="px-2 py-1 rounded bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 font-mono text-xs">"{cond.inputValue}"</span>
                                                                    <button onClick={() => handleDeleteCondition(cond.id, rule.id)} className="text-slate-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity">
                                                                        <X size={14} />
                                                                    </button>
                                                                </div>
                                                            ))}

                                                            {addingConditionToRuleId === rule.id && (
                                                                <div className="flex items-center gap-2 flex-wrap pl-4 border-l-2 border-slate-100 dark:border-slate-800 w-full animate-in fade-in duration-300">
                                                                    <CustomSelect value={newConditionSourceField} onChange={setNewConditionSourceField} options={DOC_INTELLIGENCE_FIELDS} className="min-w-[140px]" />
                                                                    <CustomSelect value={newConditionOperator} onChange={setNewConditionOperator} options={RULE_OPERATORS} className="w-32" />
                                                                    <input value={newConditionInputValue} onChange={(e) => setNewConditionInputValue(e.target.value)} className="w-40 rounded-lg border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 text-sm focus:ring-primary focus:border-primary py-2 px-3" placeholder="Compare value..." type="text" />
                                                                    <button onClick={() => handleAddCondition(rule.id)} className="h-8 w-8 flex items-center justify-center rounded-lg bg-primary text-white hover:bg-primary/90 shrink-0 shadow-sm"><Save size={16} /></button>
                                                                    <button onClick={() => setAddingConditionToRuleId(null)} className="h-8 w-8 flex items-center justify-center rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500 shrink-0"><X size={16} /></button>
                                                                </div>
                                                            )}
                                                            
                                                            {rule.conditions.length === 0 && <span className="text-xs text-slate-400 italic">(Always applies)</span>}

                                                            {addingConditionToRuleId !== rule.id && (
                                                                <button onClick={() => setAddingConditionToRuleId(rule.id)} className="text-xs font-bold text-primary hover:underline mt-2 ml-4">+ Add Condition</button>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity absolute top-4 right-4">
                                                    <button 
                                                        onClick={() => handleDeleteRule(rule.id)}
                                                        className="h-8 w-8 flex items-center justify-center rounded-lg hover:bg-red-50 dark:hover:bg-red-900/30 text-slate-400 hover:text-red-500 transition-colors"
                                                    >
                                                        <Trash2 size={16} />
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </>
                    ) : (
                        <div className="flex-1 flex flex-col items-center justify-center text-slate-400">
                            <Route size={48} className="mb-4 opacity-20" />
                            <p>Select a profile to manage mappings.</p>
                        </div>
                    )}
                </main>
            </div>

            {isModalOpen && (
                createPortal(
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-in fade-in duration-200">
                        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-xl w-full max-w-md overflow-hidden animate-in zoom-in-95 duration-200 border border-slate-200 dark:border-slate-700">
                            <div className="px-6 py-4 border-b border-slate-100 dark:border-slate-700 flex justify-between items-center">
                                <h3 className="font-bold text-lg text-slate-900 dark:text-white">New Profile</h3>
                                <button onClick={() => setIsModalOpen(false)} className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200">
                                    <X size={20} />
                                </button>
                            </div>
                            <div className="p-6 space-y-4">
                                <div>
                                    <label className="block text-xs font-bold uppercase text-slate-500 mb-1">Profile Name</label>
                                    <input 
                                        className="w-full rounded-lg border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 p-2.5 text-sm focus:ring-primary focus:border-primary text-slate-900 dark:text-white outline-none"
                                        value={newProfileName}
                                        onChange={e => setNewProfileName(e.target.value)}
                                        placeholder="e.g. Invoice Processing"
                                        autoFocus
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold uppercase text-slate-500 mb-1">Model ID</label>
                                    <input 
                                        className="w-full rounded-lg border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 p-2.5 text-sm focus:ring-primary focus:border-primary text-slate-900 dark:text-white outline-none"
                                        value={newModelId}
                                        onChange={e => setNewModelId(e.target.value)}
                                        placeholder="e.g. prebuilt-invoice"
                                    />
                                </div>
                            </div>
                            <div className="px-6 py-4 bg-slate-50 dark:bg-slate-900/50 flex justify-end gap-2 border-t border-slate-100 dark:border-slate-800">
                                <button onClick={() => setIsModalOpen(false)} className="px-4 py-2 text-sm font-bold text-slate-500 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-lg transition-colors">Cancel</button>
                                <button onClick={handleSaveProfile} className="px-4 py-2 text-sm font-bold text-white bg-primary hover:bg-primary/90 rounded-lg transition-colors shadow-lg shadow-primary/20">Create Profile</button>
                            </div>
                        </div>
                    </div>,
                    document.body
                )
            )}
        </div>
    );
}